import * as React from "react";
import {useState} from "react";
import {useTranslation} from "react-i18next";
import NotificationManager from "../../components/notifications/NotificationManager";
import WatsonApi from "../../backends/WatsonApi";
import MailToLink from "../../components/misc/MailToLink";
import {useStores} from "../../stores/useStores";

const {
    supportedLocales,
} = require("../../internationalization/SupportedLocales");

export function LanguageSettingsForm(props) {
    const {i18nStore, organizationStore} = useStores();
    const [hasChanged, setHasChanged] = useState(false);
    const {t} = useTranslation('translation');

    const [targetLanguages, setTargetLanguages] = useState<{ [locale: string]: boolean }>((Array.from(supportedLocales) as Array<string>).reduce((acc, [locale]) => {
        acc[locale] = organizationStore.organization.json_data.settings.languages.targetLanguages.indexOf(locale) > -1;
        return acc;
    }, {}))
    const organizationSupportedLocales = i18nStore.organizationSupportedLocales;
    const [defaultLocale, setDefaultLocale] = useState<string>(i18nStore.orgDefaultLocale);

    async function handleSubmit(e) {
        e.preventDefault();

        try {
            const client = await WatsonApi();
            const response = await client.apis.settings.settings_update({
                "id": organizationStore.currentOrganization.id,
                "data": {
                    "languages": {
                        "targetLanguages": Object.entries(targetLanguages).reduce((acc: Array<string>, [locale, enabled]) => {
                            if (enabled) {
                                acc.push(locale);
                            }
                            return acc;
                        }, []),
                        "sourceLanguage": defaultLocale,
                    }
                }
            });

            const settings = response.body.settings;
            if (settings) {
                organizationStore.currentOrganization.json_data.settings = settings;
                NotificationManager.success("Settings successfully updated!");
            }
        } catch (e) {
            switch (e.statusCode) {
                case 400:
                    NotificationManager.error("Bad Request: Settings not updated.");
                case 404:
                    NotificationManager.error("Not Found: Organization settings not found.");
                case 500:
                    NotificationManager.error("Internal Server Error: Settings not updated.");
                default:
                    NotificationManager.error("Unknown Error: Settings not updated.");
            }
        }
    }

    return <form id="district-appearance-form" onSubmit={handleSubmit} onChange={() => setHasChanged(true)}>
        <div className="panel panel-default">
            <div className="panel-heading">
                <h3 className="panel-title text-center">{t('Translation Options')}</h3>
            </div>
            <div className="panel-body">
                <div className="row">
                    <div className="col-sm-4">
                        <label>{t('Default Language')}</label>
                    </div>
                    <div className="col-sm-8">
                        {organizationSupportedLocales.map((locale) => {
                            const language = supportedLocales.get(locale);
                            return <div key={locale}>
                                <input
                                    type={"radio"}
                                    className={'form-control'}
                                    style={{display: "inline-flex", width: "1em", height: "auto"}}
                                    checked={locale === defaultLocale}
                                    title={`Default Language`}
                                    id={locale}
                                    onChange={() => {setDefaultLocale(locale)}}
                                />
                                <label htmlFor={locale}>
                                    {language._englishName} / {language.language} ({locale})
                                </label>
                            </div>
                        })}
                    </div>
                </div>
                <div className="row">
                    <div className="col-sm-4">
                        <div className="row" style={{margin: "0 0 0 0"}}>
                            <label>{t('Target Languages:')}</label>
                        </div>
                        <div className="row" style={{marginLeft: 0}}>
                            <small>{t('Supported Translation Languages')}</small>
                        </div>
                    </div>
                    <div className="col-sm-8">
                        {Object.entries(targetLanguages).map(([locale, value]) => {
                            const language = supportedLocales.get(locale);
                            return <div key={locale}>
                                <input
                                    type={"checkbox"}
                                    className={'form-control'}
                                    style={{display: "inline-flex", width: "1em", height: "auto"}}
                                    checked={targetLanguages[locale]}
                                    title={`Target Language ${language._englishName}`}
                                    id={locale}
                                    onChange={() => {
                                        setTargetLanguages(v => ({
                                            ...v,
                                            [locale]: !targetLanguages[locale],
                                        }))
                                    }}
                                />
                                <label
                                    htmlFor={locale}>{language._englishName} / {language.language} ({locale})</label>
                            </div>
                        })}
                    </div>
                </div>
            </div>
        </div>
        <div className="sb-settings-block-footer">
            <button type="submit" className="btn sb-organization-color-element-bg" name="submit"
                    disabled={!hasChanged}>Save
            </button>
        </div>
    </form>
}

export function LanguageSettingsTab(props) {
    const {t} = useTranslation('translation');

    return <div id="language-tab">
        <header className="sb-settings-block-content-heading">
            <h4>{t('Language')}</h4>
        </header>
        <p>
            If your school provides translation services, please select the languages you actively provide translation
            services for. Do not select languages that you do not offer translation services for! By selecting
            an additional language, content creation tools will expand to allow for additional language options.
            Please note that selecting a language complicates the admin experience. Please contact
            <MailToLink recipient={"support@schoolblocks.com"}>{" support@schoolblocks.com "}</MailToLink>
            for any questions or if you are interested in translation services.
        </p>
        <LanguageSettingsForm/>
    </div>
}
